import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { curlFor, highlightJson, normalizeRequestPath, truncateArray } from "../../pages/apipage_utils.js";
import { copyText } from "./clipboard.js";

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 4l10 6-10 6z" /></svg>
  );
}

function CopyButton({ label, getText, disabled }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="apiPanelLink"
      disabled={disabled}
      onClick={async () => {
        const ok = await copyText(getText());
        if (ok) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }
      }}
    >
      <span aria-live="polite">{copied ? "Copied" : label}</span>
    </button>
  );
}

/**
 * The live half of the page. Runs a real GET for `requestPath` whenever it changes and
 * shows status, record count, elapsed time and the highlighted body (first 50 rows).
 * Editing the request line hands the normalized path back up via onRequestPathChange;
 * the page owns the path so the URL and the active op row stay in sync with it.
 */
export function ApiResponsePanel({ requestPath, onRequestPathChange, onEscape }) {
  const [draft, setDraft] = useState(requestPath);
  const [state, setState] = useState({ phase: "idle" });
  const [runCount, setRunCount] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(requestPath); }, [requestPath]);

  useEffect(() => {
    let cancelled = false;
    const started = performance.now();
    setState({ phase: "loading" });
    axios.get(requestPath).then(
      (res) => {
        if (cancelled) return;
        const ms = Math.round(performance.now() - started);
        const count = Array.isArray(res.data) ? res.data.length : 1;
        setState({ phase: "ok", status: res.status, statusText: res.statusText || "OK", ms, count, data: res.data });
      },
      (err) => {
        if (cancelled) return;
        const ms = Math.round(performance.now() - started);
        if (err && err.response) {
          const detail = err.response.data && err.response.data.detail;
          setState({ phase: "error", status: err.response.status, statusText: err.response.statusText || "",
            message: detail || "Request failed", ms, data: err.response.data });
        } else {
          setState({ phase: "network", message: "Could not reach the server", ms });
        }
      }
    );
    return () => { cancelled = true; };
  }, [requestPath, runCount]);

  const submit = (e) => {
    e.preventDefault();
    const next = normalizeRequestPath(draft);
    setDraft(next);
    if (next === requestPath) setRunCount((n) => n + 1);
    else onRequestPathChange(next);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setDraft(requestPath);
      if (onEscape) onEscape();
      else inputRef.current && inputRef.current.blur();
    }
  };

  // Truncation + highlighting touch every byte of the response; only redo them when the
  // data itself changes, not on every keystroke in the request line or copy-button click.
  const { html, hidden } = useMemo(() => {
    const { shown, hidden: hiddenCount } = truncateArray(state.data);
    const json = shown !== undefined ? JSON.stringify(shown, null, 2) : "";
    return { html: json ? highlightJson(json) : "", hidden: hiddenCount };
  }, [state.data]);

  const hasData = state.data !== undefined;


  return (
    <aside className="apiResponsePanel" aria-label="Live response">
      <div className="apiResponseInner">
        <div className="apiResponseHead">
          <span className="apiEyebrow">Live response</span>
          <span className="apiStatusStrip" role="status">
            {state.phase === "loading" && <span>Running…</span>}
            {state.phase === "ok" && (
              <>
                <span className="apiStatusCode"><span className="apiStatusDot" aria-hidden="true" />{state.status} {state.statusText}</span>
                <span>{state.count} {state.count === 1 ? "record" : "records"}</span>
                <span>{state.ms} ms</span>
              </>
            )}
            {state.phase === "error" && (
              <>
                <span className="apiStatusCode"><span className="apiStatusDot apiStatusDotError" aria-hidden="true" />{state.status} {state.statusText}</span>
                <span>{state.message}</span>
              </>
            )}
            {state.phase === "network" && (
              <>
                <span className="apiStatusCode"><span className="apiStatusDot apiStatusDotError" aria-hidden="true" />No response</span>
                <span>{state.message}</span>
              </>
            )}
          </span>
        </div>

        <form className="apiRequestForm" onSubmit={submit}>
          <label className="apiRequestLine">
            <span className="apiRequestMethod" aria-hidden="true">GET</span>
            <input
              ref={inputRef}
              className="apiRequestInput"
              type="text"
              aria-label="Request path"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              spellCheck="false"
              autoComplete="off"
            />
          </label>
          <button type="submit" className="apiRunButton" disabled={state.phase === "loading"}>
            <PlayIcon /> Run
          </button>
        </form>
        <p className="apiResponseHint">Edit the query, then run it. The response below is real data from this server.</p>

        {html && (
          <pre className="apiJson" tabIndex="0" aria-label="Response body"
            dangerouslySetInnerHTML={{ __html: html }} />
        )}
        {hidden > 0 && <p className="apiJsonMore">… {hidden} more. Open raw for the full response.</p>}

        <div className="apiResponseActions">
          <CopyButton label="Copy JSON" getText={() => JSON.stringify(state.data, null, 2)} disabled={!hasData} />
          {hasData && (
            <a className="apiPanelLink" href={requestPath} target="_blank" rel="noreferrer">Open raw</a>
          )}
          <CopyButton label="Copy as curl" getText={() => curlFor(window.location.origin, requestPath)} disabled={!hasData} />
        </div>
      </div>
    </aside>
  );
}
